<template>
  <section
    class="hv6"
    ref="heroRef"
    :aria-label="$t('page.hero.ariaSection')"
    aria-roledescription="Hero"
  >
    <div class="hv6__grid">
      <figure class="hv6__photo-col" ref="photoRef">
        <img
          class="hv6__photo"
          :src="profileImage"
          :alt="$t('page.hero.photoAlt')"
          width="800"
          height="1200"
          draggable="false"
          loading="eager"
        />
        <figcaption class="sr-only">{{ $t('page.hero.photoAlt') }}</figcaption>
      </figure>

      <div class="hv6__body" ref="bodyRef">
        <p class="hv6__role" ref="roleRef">
          <span class="hv6__role-text">{{ $t('page.hero.label') }}</span>
        </p>

        <h1 class="hv6__headline">
          <span class="hv6__line hv6__line--1">{{ $t('page.hero.headline.line1') }}</span>
          <span class="hv6__line hv6__line--2">
            <em class="hv6__em" ref="emRef">{{ emWord }}</em>
          </span>
          <span class="hv6__line hv6__line--3">{{ $t('page.hero.headline.line3') }}</span>
        </h1>
      </div>
    </div>

    <div class="hv6__scroll-indicator" ref="scrollRef" aria-hidden="true">
      <div class="hv6__drop-wrap">
        <div class="hv6__drop-track">
          <div class="hv6__drop-pearl"></div>
        </div>
      </div>

      <svg
        class="hv6__wave"
        width="440"
        height="64"
        viewBox="0 0 440 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0 1.06152C8.50765 1.14874 17.0153 1.23097 25.5229 1.30821C70.2222 1.71404 114.921 1.98219 159.621 2.11267C175.444 1.7794 189.637 16.0485 189.241 31.8207C189.241 32.1195 189.241 32.4183 189.241 32.7172C189.231 37.6761 190.432 42.6344 192.731 47.0313C197.88 57.1114 208.914 63.8414 220.243 63.709C236.755 64.0948 251.599 49.1868 251.161 32.7172C251.161 32.4183 251.16 32.1195 251.159 31.8207C250.736 16.0042 264.966 1.65145 280.864 1.97582C325.219 1.81759 369.575 1.57756 413.93 1.25574C422.437 1.19401 430.945 1.12927 439.453 1.06152C430.945 0.993777 422.437 0.929039 413.93 0.867311C369.575 0.545486 325.219 0.305457 280.864 0.147223C263.995 -0.325175 248.678 14.8687 249.051 31.8207C249.05 32.1195 249.05 32.4183 249.049 32.7172C249.385 48.0609 235.543 61.8384 220.243 61.4506C209.736 61.5538 199.532 55.311 194.76 45.9737C192.631 41.9015 191.516 37.3186 191.519 32.7172C191.519 32.4183 191.519 32.1195 191.518 31.8207C191.92 14.8244 176.566 -0.453112 159.621 0.0103794C114.921 0.140854 70.2222 0.409006 25.5229 0.814835C17.0153 0.892077 8.50765 0.974307 0 1.06152Z"
          fill="currentColor"
        />
      </svg>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import i18n from '@/i18n'
import photoDark from '@/assets/images/juan-lias-front.webp'
import photoLight from '@/assets/images/juan-lias-front-light.webp'
import { useTheme } from '@/composables/useTheme'

gsap.registerPlugin(ScrollTrigger)

const { isDark } = useTheme()
const { t } = useI18n()
const profileImage = computed(() => (isDark.value ? photoDark : photoLight))

const heroRef = ref(null)
const bodyRef = ref(null)
const roleRef = ref(null)
const scrollRef = ref(null)
const emRef = ref(null)
const photoRef = ref(null)
const emWord = ref('')

const GLOW_OFF =
  'brightness(1) drop-shadow(0px 0 0px rgba(255,255,255,0)) drop-shadow(0px 0 0px rgba(167,139,250,0))'
const GLOW_PEAK =
  'brightness(1.7) drop-shadow(0px 0 20px rgba(255,255,255,1)) drop-shadow(0px 0 52px rgba(167,139,250,0.9)) drop-shadow(0px 0 88px rgba(124,58,237,0.55))'
const GLOW_END =
  'brightness(1) drop-shadow(0px 0 0px rgba(255,255,255,0)) drop-shadow(0px 0 0px rgba(167,139,250,0))'

function shuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

const buildWordList = () => {
  const locale = i18n.global.locale.value
  const words = i18n.global.messages.value[locale]?.page?.hero?.cyclingWords || []
  return [...shuffle(words), t('page.hero.headline.em')]
}

let ctx = null
let wordList = []
let cycleDelayedCall = null
const isMobile = window.matchMedia('(max-width: 767px)').matches

function runWordCycle() {
  if (!emRef.value) return
  if (cycleDelayedCall) {
    cycleDelayedCall.kill()
    cycleDelayedCall = null
  }
  gsap.killTweensOf(emRef.value, 'opacity,y,filter')
  const el = emRef.value
  let idx = 0

  function advance() {
    const nextIdx = idx + 1
    if (nextIdx >= wordList.length) return

    const isLast = nextIdx === wordList.length - 1
    const t = idx / Math.max(1, wordList.length - 3)
    const holdMs = Math.round(Math.max(55, 185 - t * 130))
    const exitMs = Math.round(holdMs * 0.48)
    const enterMs = Math.round(holdMs * 0.48)

    gsap.to(el, {
      opacity: 0,
      y: -12,
      filter: 'blur(10px)',
      duration: exitMs / 1000,
      ease: 'power3.in',
      onComplete() {
        emWord.value = wordList[nextIdx]

        nextTick(() => {
          gsap.set(el, { y: 0, filter: 'blur(0px)' })

          if (isLast) {
            gsap.fromTo(
              el,
              { opacity: 0, y: 18 },
              { opacity: 1, y: 0, duration: 0.85, ease: 'power4.out' }
            )
            gsap
              .timeline({
                onComplete: () => gsap.set(el, { clearProps: 'filter' }),
              })
              .fromTo(
                el,
                { filter: GLOW_OFF },
                { filter: GLOW_PEAK, duration: 0.1, ease: 'power4.in' }
              )
              .to(el, { filter: GLOW_END, duration: 1.2, ease: 'power2.out' })
          } else {
            gsap.fromTo(
              el,
              { opacity: 0, y: 10, filter: 'blur(8px)' },
              {
                opacity: 1,
                y: 0,
                filter: 'blur(0px)',
                duration: enterMs / 1000,
                ease: 'power3.out',
              }
            )
            idx = nextIdx
            cycleDelayedCall = gsap.delayedCall(holdMs / 1000, advance)
          }
        })
      },
    })
  }

  advance()
}

const restartWordCycle = () => {
  if (!emRef.value) return
  gsap.killTweensOf(emRef.value)
  if (cycleDelayedCall) {
    cycleDelayedCall.kill()
    cycleDelayedCall = null
  }
  wordList = buildWordList()
  emWord.value = wordList[0]
  gsap.set(emRef.value, { opacity: 1, y: 0, filter: 'blur(0px)' })
  cycleDelayedCall = gsap.delayedCall(1.0, runWordCycle)
}

function onMouseMove(e) {
  if (!heroRef.value || !emRef.value || isMobile) return
  const rect = heroRef.value.getBoundingClientRect()
  const x = ((e.clientX - rect.left) / rect.width) * 100
  const y = ((e.clientY - rect.top) / rect.height) * 100
  gsap.to(emRef.value, {
    duration: 0.6,
    ease: 'power2.out',
    backgroundPosition: `${x}% ${y}%`,
  })
}

function onMouseLeave() {
  if (!emRef.value || isMobile) return
  gsap.to(emRef.value, {
    duration: 1.2,
    ease: 'power3.out',
    backgroundPosition: '50% 50%',
  })
}

onMounted(() => {
  wordList = buildWordList()
  emWord.value = wordList[0]

  ctx = gsap.context(() => {
    const lines = gsap.utils.toArray(bodyRef.value?.querySelectorAll('.hv6__line'))
    const wave = scrollRef.value?.querySelector('.hv6__wave')
    const track = scrollRef.value?.querySelector('.hv6__drop-wrap')

    gsap.set(roleRef.value, { opacity: 0, y: -24, filter: 'blur(12px)' })
    gsap.set(lines, { opacity: 0, y: -48, filter: 'blur(12px)' })
    gsap.set(scrollRef.value, { opacity: 1 })
    if (wave) gsap.set(wave, { opacity: 0, y: -40 })
    if (track) gsap.set(track, { opacity: 0, y: -40 })
    if (photoRef.value) gsap.set(photoRef.value, { opacity: 0, scale: 0.94, x: -30 })

    ScrollTrigger.create({
      trigger: heroRef.value,
      start: 'top top',
      end: 'bottom top',
      scrub: 0.5,
      onUpdate(self) {
        const y = self.scroll()
        const p = self.progress
        const blur = p * 8

        if (bodyRef.value) {
          gsap.set(bodyRef.value, {
            y: -y * 0.1,
            opacity: Math.max(0, 1 - p * 1.15),
            filter: `blur(${blur}px)`,
            force3D: true,
          })
        }
        if (photoRef.value) {
          gsap.set(photoRef.value, {
            y: y * 0.04,
            opacity: Math.max(0, 1 - p * 2),
            filter: `blur(${p * 8}px)`,
            force3D: true,
          })
        }
        if (scrollRef.value) {
          gsap.set(scrollRef.value, {
            opacity: Math.max(0, 1 - y / 180),
            force3D: true,
          })
        }
      },
    })

    const hasPreloader = document.querySelector('.preloader') && !window.__PRELOADER_DONE__

    const entranceTl = gsap.timeline({
      paused: hasPreloader,
      defaults: { ease: 'power3.out' },
    })

    if (photoRef.value) {
      entranceTl.to(
        photoRef.value,
        { opacity: 1, scale: 1, x: 0, duration: 1.8, ease: 'power2.out' },
        1.2
      )
    }

    entranceTl
      .to(roleRef.value, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 1.1 }, 0.2)
      .to(lines, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 1.1, stagger: 0.12 }, 0.5)
      .call(runWordCycle, [], 1.8)
      .to(wave, { opacity: 0.15, y: 0, duration: 1.2 }, 1.3)
      .to(track, { opacity: 1, y: 0, duration: 1.2 }, 1.8)

    if (hasPreloader) {
      window.addEventListener('preloader:done', () => entranceTl.play(), { once: true })
    }
  }, heroRef.value)

  heroRef.value?.addEventListener('mousemove', onMouseMove)
  heroRef.value?.addEventListener('mouseleave', onMouseLeave)
})

onUnmounted(() => {
  heroRef.value?.removeEventListener('mousemove', onMouseMove)
  heroRef.value?.removeEventListener('mouseleave', onMouseLeave)
  if (cycleDelayedCall) {
    cycleDelayedCall.kill()
    cycleDelayedCall = null
  }
  if (ctx) ctx.revert()
})

watch(
  () => i18n.global.locale.value,
  () => {
    restartWordCycle()
  }
)
</script>

<style scoped>
/* ══ ROOT ══════════════════════════════════════════════════════════ */
.hv6 {
  position: relative;
  min-height: 100svh;
  overflow: hidden;
  background-color: var(--color-deep-black);
  display: flex;
  flex-direction: column;
  transition: background-color 0.3s ease;
}

/* ══ GRID ══════════════════════════════════════════════════════════ */
.hv6__grid {
  position: relative;
  z-index: 1;
  flex: 1;
  width: 100%;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: grid;
  grid-template-rows: 1fr;
  grid-template-columns: 1fr;
  grid-template-areas: 'body';
  min-height: 100svh;
}

@media (min-width: 768px) {
  .hv6__grid {
    padding: 0 3rem 0 0;
    grid-template-columns: 42% 1fr;
    grid-template-areas: 'photo body';
  }
}

@media (min-width: 1024px) {
  .hv6__grid {
    padding: 0 4rem 0 0;
    grid-template-columns: 42% 1fr;
  }
}

@media (min-width: 1280px) {
  .hv6__grid {
    grid-template-columns: 40% 1fr;
  }
}

/* ══ ROLE ══════════════════════════════════════════════════════════ */
.hv6__role {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 1rem;
  opacity: 0;
}

.hv6__role-text {
  font-size: 0.67rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--color-steel);
  transition: color 0.3s ease;
}

/* ══ BODY (headline) ═══════════════════════════════════════════════ */
.hv6__body {
  grid-area: body;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.5rem;
  padding-top: 4rem;
  padding-bottom: 8vh;
}

@media (min-width: 768px) {
  .hv6__body {
    padding-top: 6rem;
    padding-left: 0.5rem;
    margin-left: -8rem;
  }
}

@media (min-width: 1024px) {
  .hv6__body {
    padding-left: 1rem;
    margin-left: -12rem;
  }
}

@media (min-width: 1280px) {
  .hv6__body {
    margin-left: -16rem;
  }
}

/* Headline */
.hv6__headline {
  margin: 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 700;
  letter-spacing: -0.035em;
  line-height: 0.88;
  font-size: clamp(2.8rem, 7.5vw, 8.5rem);
  color: var(--color-text-primary);
  display: flex;
  flex-direction: column;
  transition: color 0.3s ease;
}

.hv6__line {
  display: block;
  opacity: 0;
}

.hv6__line--2 {
  line-height: 1.05;
  padding-block: 0.02em;
  margin-bottom: -10px;
  margin-top: -10px;
}

.hv6__em {
  @apply font-madi italic;
  letter-spacing: normal;
  font-size: 120%;
  background: linear-gradient(
    110deg,
    var(--color-electric-purple) 0%,
    #a78bfa 50%,
    var(--color-electric-purple) 100%
  );
  background-size: 400% 400%;
  background-position: 50% 50%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  padding-right: 30px;
  padding-left: 45px;
  margin-left: -45px;
  will-change: background-position;
}

/* ══ PHOTO COLUMN (left side, md+) ═════════════════════════════════ */
.hv6__photo-col {
  grid-area: photo;
  display: none;
  position: relative;
  overflow: hidden;
}

@media (min-width: 1024px) {
  .hv6__photo-col {
    display: block;
  }
}

.hv6__photo {
  @apply opacity-[0.7] dark:opacity-[0.3];
  position: absolute;
  inset: 0 auto 0 0;
  height: 100%;
  width: auto;
  display: block;
  user-select: none;
  pointer-events: none;
  mask-image:
    radial-gradient(circle at 0% 50%, black 0%, black 50%, transparent 77%),
    linear-gradient(to left, transparent 0%, black 31%, black 100%);
  -webkit-mask-image:
    radial-gradient(circle at 0% 50%, black 0%, black 50%, transparent 77%),
    linear-gradient(to left, transparent 0%, black 31%, black 100%);
  -webkit-mask-composite: source-in;
  mask-composite: intersect;
}

/* ══ SCROLL INDICATOR ═══════════════════════════════════════════════ */
.hv6__scroll-indicator {
  position: absolute;
  bottom: 2.25rem;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  opacity: 0;
  transform: scale(0.7);
}

.hv6__wave {
  position: absolute;
  bottom: -25px;
  width: min(304px, 64vw);
  height: auto;
  display: block;
  color: var(--color-text-primary);
  opacity: 0.15;
}

.hv6__drop-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0;
}

/* Vertical rail */
.hv6__drop-track {
  width: 1px;
  height: 56px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    var(--color-border) 25%,
    rgba(124, 58, 237, 0.5) 100%
  );
  position: relative;
  overflow: hidden;
}

/* Glowing pearl that drops */
.hv6__drop-pearl {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 3px;
  height: 10px;
  border-radius: 3px;
  background: linear-gradient(to bottom, #a78bfa, var(--color-electric-purple));
  box-shadow:
    0 0 8px var(--color-electric-purple),
    0 0 20px rgba(124, 58, 237, 0.5);
  animation: hv6-fall 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes hv6-fall {
  0% {
    top: -10px;
    opacity: 0;
  }
  12% {
    opacity: 1;
  }
  88% {
    opacity: 1;
  }
  100% {
    top: 100%;
    opacity: 0;
  }
}

/* Screen-reader only utility */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
